'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon, PauseIcon, PlayIcon, SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { fetcher } from '@utils/data-fetching/fetcher';
import { useTranslation } from 'react-i18next';
import type { ApiSchema } from '@type/api/apiUtilityFn';

type AudioResponse = ApiSchema<'InterviewAudioOutput'>;

interface InterviewAudioPlayerProps {
  interviewId: number;
}

/**
 * Audio player component for interview recordings
 */
export const InterviewAudioPlayer = ({ interviewId }: InterviewAudioPlayerProps) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Fetch the audio URL from the API
  useEffect(() => {
    fetcher<AudioResponse>(`/v1/screening/interviews/${interviewId}/audio`, undefined)
      .then((response) => {
        if (response && response.audioUrl) {
          setAudioUrl(response.audioUrl);
        } else {
          setError(true);
          setIsLoading(false);
        }
      })
      .catch(() => {
        setError(true);
        setIsLoading(false);
      });
  }, [interviewId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return undefined;

    // reset UI for the new source
    setIsLoading(true);
    setError(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setError(true);
      setIsLoading(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // important when changing src dynamically
    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handlePlaybackRateChange = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const rates = [1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    audio.playbackRate = nextRate;
    setPlaybackRate(nextRate);
  };

  const formatTime = (time: number) => {
    if (!Number.isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
        {t('screening.audio.empty')}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <audio ref={audioRef} src={audioUrl ?? undefined} preload="metadata" />

      {/* Progress bar */}
      <div className="mb-3">
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={currentTime}
          onChange={handleSeek}
          disabled={isLoading}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-500"
        />
        <div className="mt-1 flex justify-between text-xs text-slate-500">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Play/Pause button */}
          <button
            type="button"
            onClick={togglePlay}
            disabled={isLoading}
            className="flex size-10 items-center justify-center rounded-full bg-white shadow-sm transition-colors hover:bg-slate-100 disabled:opacity-50"
          >
            {isPlaying ? (
              <PauseIcon className="size-5 text-slate-700" />
            ) : (
              <PlayIcon className="size-5 text-slate-700" />
            )}
          </button>

          {/* Volume icon */}
          <SpeakerWaveIcon className="size-5 text-slate-400" />
        </div>

        <div className="flex items-center gap-3">
          {/* Playback speed */}
          <button
            type="button"
            onClick={handlePlaybackRateChange}
            disabled={isLoading}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            <span className="text-slate-400">⟳</span>
            {playbackRate}
            x
          </button>

          {/* Download button */}
          <a
            href={audioUrl ?? undefined}
            download={`interview-${interviewId}.mp3`}
            target="_blank"
            className="flex size-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
            rel="noreferrer"
          >
            <ArrowDownTrayIcon className="size-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default InterviewAudioPlayer;
